import { useEffect, useState } from "react";
import type { UnlockItem } from "../local/unlocks";

interface Props {
  items: UnlockItem[];
  /** Called when the user picks "Wear it" — caller applies the cosmetic to
   *  the active profile's AvatarConfig. */
  onWear?: (item: UnlockItem) => void;
  onDone: () => void;
}

/**
 * Post-run unlock reveal. Shows each newly-earned cosmetic one at a time
 * with a flip-in animation + confetti, then offers a "Wear it" prompt so
 * the player can equip it immediately. Tapping "Not now" or finishing the
 * queue closes the overlay.
 */
export function UnlockReveal({ items, onDone, onWear }: Props) {
  const [index, setIndex] = useState(0);
  const item = items[index];
  const [flipped, setFlipped] = useState(false);

  useEffect(() => {
    setFlipped(false);
    // Trigger the flip-in animation on next tick.
    const t = window.setTimeout(() => setFlipped(true), 50);
    return () => window.clearTimeout(t);
  }, [index]);

  if (!item) return null;

  const advance = () => {
    if (index + 1 >= items.length) onDone();
    else setIndex(index + 1);
  };
  const wearAndAdvance = () => {
    onWear?.(item);
    advance();
  };

  return (
    <div className="unlock-overlay" role="dialog" aria-modal="true" aria-label="New item unlocked">
      <Confetti />
      <div className="unlock-banner">NEW ITEM UNLOCKED!</div>
      <div className={`unlock-card ${flipped ? "is-in" : ""}`}>
        <div className="unlock-card-inner">
          <div className="unlock-thumb">
            <UnlockThumb item={item} />
          </div>
          <div className="unlock-name">{item.name}</div>
          <div className="unlock-kind">{kindLabel(item.kind)}</div>
        </div>
      </div>
      <div className="unlock-progress">
        {items.map((_, i) => (
          <span key={i} className={`unlock-dot ${i <= index ? "is-on" : ""}`} />
        ))}
      </div>
      <div className="unlock-prompt">Wear it now?</div>
      <div className="unlock-actions">
        <button className="btn btn-ghost btn-big unlock-action-skip" onClick={advance}>
          Not now
        </button>
        <button className="btn btn-primary btn-big unlock-action-wear" onClick={wearAndAdvance}>
          ✨ Wear it
        </button>
      </div>
    </div>
  );
}

function kindLabel(kind: UnlockItem["kind"]): string {
  if (kind === "hat") return "HAT";
  if (kind === "pattern") return "PATTERN";
  return "SHIRT COLOR";
}

function UnlockThumb({ item }: { item: UnlockItem }) {
  if (item.kind === "hat") return <HatThumb id={item.apply.hatId ?? "none"} />;
  if (item.kind === "pattern") return <PatternThumb id={item.apply.patternId ?? "solid"} />;
  return <ColorThumb color={item.apply.shirtColor ?? "#ff2ca4"} />;
}

function HatThumb({ id }: { id: string }) {
  const emoji =
    id === "cap-blue" ? "🧢" :
    id === "cap-red" ? "🧢" :
    id === "cap-green" ? "🧢" :
    id === "cap-purple" ? "🧢" :
    id === "beanie" ? "🎩" :
    id === "beanie-blue" ? "🎩" :
    id === "crown" ? "👑" :
    id === "party-hat" ? "🥳" :
    id === "halo" ? "😇" :
    id === "top-hat" ? "🎩" :
    id === "wizard" ? "🧙" :
    id === "horns" ? "😈" :
    id === "headband" ? "🎀" :
    id === "bow" ? "🎀" : "🚫";
  const tint =
    id === "cap-blue" ? "#2563eb" :
    id === "cap-red" ? "#dc2626" :
    id === "cap-green" ? "#16a34a" :
    id === "cap-purple" ? "#9b5cf6" :
    id === "beanie" ? "#ef476f" :
    id === "beanie-blue" ? "#3b82f6" :
    id === "crown" ? "#ffd166" :
    id === "party-hat" ? "#a855f7" :
    id === "halo" ? "#fef08a" :
    id === "top-hat" ? "#1f2937" :
    id === "wizard" ? "#7c3aed" :
    id === "horns" ? "#ef4444" :
    id === "headband" ? "#ff7a59" :
    id === "bow" ? "#ec4899" : "#bbb";
  return (
    <div className="unlock-thumb-hat" style={{ background: tint }}>
      <span>{emoji}</span>
    </div>
  );
}

function PatternThumb({ id }: { id: string }) {
  const bg =
    id === "stripes"
      ? "repeating-linear-gradient(45deg, #ff2ca4 0 14px, #ffffff 14px 28px)"
      : id === "dots"
      ? "radial-gradient(#ffffff 18%, transparent 22%) #ff2ca4"
      : id === "star"
      ? "radial-gradient(circle at 50% 50%, #ffd166 18%, transparent 22%) #ff2ca4"
      : id === "checker"
      ? "repeating-conic-gradient(#ff2ca4 0 25%, #ffffff 0 50%) 50% / 30px 30px"
      : id === "zigzag"
      ? "repeating-linear-gradient(135deg, #ff2ca4 0 10px, #ffe066 10px 20px)"
      : id === "hearts"
      ? "radial-gradient(#ffffff 22%, transparent 26%) #ff2ca4"
      : id === "lightning"
      ? "linear-gradient(45deg, #ffd166, #ff2ca4, #ffd166)"
      : "#ff2ca4";
  return (
    <div
      className="unlock-thumb-pat"
      style={{ background: bg, backgroundSize: id === "dots" || id === "star" || id === "hearts" ? "32px 32px" : "auto" }}
    />
  );
}

function ColorThumb({ color }: { color: string }) {
  return <div className="unlock-thumb-color" style={{ background: color }} />;
}

function Confetti() {
  const pieces = Array.from({ length: 36 });
  return (
    <div className="unlock-confetti" aria-hidden>
      {pieces.map((_, i) => (
        <span
          key={i}
          style={{
            left: `${(i * 2.78 + (i * 17) % 5) % 100}%`,
            animationDelay: `${(i % 9) * 70}ms`,
            background: ["#ff5fa7", "#6bd0ff", "#ffd166", "#7d3df0", "#5cf2c2"][i % 5],
          }}
        />
      ))}
    </div>
  );
}
